import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  TextInput,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { organizationsApi } from '../../services/apiService';
import { OrganizationList } from '../../types';
import { COLORS, LABELS } from '../../constants/config';
import { Card, LoadingScreen, EmptyState, PageContainer, Navbar, Breadcrumb } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useTranslation } from 'react-i18next';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

export default function OrganizationsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { selectedYear } = useYear();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    searchBg: isDark ? colors.backgroundAlt : '#FFFFFF',
    searchInput: colors.textPrimary,
    name: colors.textPrimary,
    responsible: colors.textMuted,
    iconBg: isDark ? colors.primaryLight : colors.primary + '15',
  };
  const [organizations, setOrganizations] = useState<OrganizationList[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      setError(null);
      const params: Record<string, any> = {};
      if (searchQuery) params.search = searchQuery;

      const response = await organizationsApi.list(params);
      setOrganizations(response.results);
    } catch (err: any) {
      setError(err.message || t('organizations.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Refetch when year changes
  useEffect(() => {
    fetchOrganizations();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchOrganizations();
  };

  const renderItem = ({ item }: { item: OrganizationList }) => (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, borderWidth: 1 }]}
      onPress={() => navigation.navigate('OrganizationDetail', { id: item.id })}
    >
      <View style={styles.cardContent}>
        {item.logo ? (
          <Image source={{ uri: item.logo }} style={styles.logo} />
        ) : (
          <View style={[styles.logoPlaceholder, { backgroundColor: themeColors.iconBg }]}>
            <Ionicons name="business" size={32} color={colors.primary} />
          </View>
        )}
        <View style={styles.info}>
          <Text style={[styles.name, { color: themeColors.name }]}>{item.name}</Text>
          {item.responsible_user_name && (
            <Text style={[styles.responsible, { color: themeColors.responsible }]}>
              {t('organizations.responsible')}: {item.responsible_user_name}
            </Text>
          )}
        </View>
        <Ionicons name="chevron-back" size={24} color={colors.iconDefault} />
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('nav.home') },
          { label: t('nav.organizations') },
        ]}
      />
      {/* Search */}
      <View style={[styles.searchContainer, { backgroundColor: themeColors.searchBg, borderColor: colors.border }]}>
        <TextInput
          style={[styles.searchInput, { color: themeColors.searchInput }]}
          placeholder={LABELS.search}
          placeholderTextColor={colors.textMuted}
          value={searchQuery}
          onChangeText={setSearchQuery}
          onSubmitEditing={fetchOrganizations}
          textAlign="right"
        />
        <Ionicons name="search" size={20} color={colors.iconDefault} style={styles.searchIcon} />
      </View>

      {error ? (
        <EmptyState
          icon="alert-circle-outline"
          title={LABELS.error}
          message={error}
          actionLabel={LABELS.refresh}
          onAction={fetchOrganizations}
        />
      ) : organizations.length === 0 ? (
        <EmptyState
          icon="business-outline"
          title={LABELS.noData}
          message={t('organizations.noOrganizationsRegistered')}
        />
      ) : (
        <FlatList
          data={organizations}
          renderItem={renderItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    margin: 16,
    borderRadius: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
    color: COLORS.text,
  },
  searchIcon: {
    marginLeft: 8,
  },
  list: {
    padding: 16,
    paddingTop: 0,
  },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    marginBottom: 12,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
    overflow: 'hidden',
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  logo: {
    width: 56,
    height: 56,
    borderRadius: 8,
    marginLeft: 12,
  },
  logoPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 8,
    backgroundColor: COLORS.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  info: {
    flex: 1,
    alignItems: 'flex-end',
  },
  name: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.text,
  },
  responsible: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginTop: 4,
  },
});
